import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  ForwardedRef,
  Fragment,
  forwardRef,
  useCallback,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { NavLink, NavLinkProps } from 'react-router-dom';

import { NavigationLevelPathConfig } from '~/components/Sidebar/components/SidebarMenuLevel/types';
import { theme } from '~/theme';

type PropsType = {
  navigationRootPath: string;
  parentLevels: string[];
  levelPaths: NavigationLevelPathConfig[];
};

const CustomNavLink = forwardRef(
  (props: NavLinkProps, ref: ForwardedRef<HTMLAnchorElement>) => (
    <NavLink ref={ref} {...props} activeClassName={'Mui-selected'} />
  ),
);

export const SidebarMenuLevel = (props: PropsType) => {
  const { navigationRootPath, parentLevels, levelPaths } = props;

  const { pathname } = useLocation();

  const [open, setOpen] = useState<string[]>([]);

  const level = parentLevels.length;

  const filterDisabled = useCallback((path: NavigationLevelPathConfig) => {
    return !(path.hasOwnProperty('isAvailable') && !path.isAvailable);
  }, []);

  const getPathTo = useCallback(
    (path: string) => {
      const parentPath =
        parentLevels.length > 0 ? `${parentLevels.join('/')}/` : '';

      return `${navigationRootPath}/${parentPath}${path}`;
    },
    [navigationRootPath, parentLevels],
  );

  const getItemKey = useCallback((level: number, index: number) => {
    return `nav-item-${level}-${index}`;
  }, []);

  const isOpen = useCallback(
    (path: string) => {
      return open.includes(path);
    },
    [open],
  );

  const handleOpen = useCallback(
    (path: string) => {
      if (isOpen(path)) {
        setOpen(open.filter((p) => p !== path));
      } else {
        setOpen([...open, path]);
      }
    },
    [isOpen, open, setOpen],
  );

  const isActivePath = useCallback(
    (pathTo: string): boolean => {
      // Add slash at end to ensure comparison of exact path segments.
      return pathname ? `${pathname}/`.startsWith(`${pathTo}/`) : false;
    },
    [pathname],
  );

  return (
    <List sx={{ padding: theme.spacing(0, 0, 0, level) }}>
      {levelPaths.filter(filterDisabled).map((item, index) => {
        const pathTo = getPathTo(item.path);
        const key = getItemKey(level, index);

        if (!!item.subPaths && item.subPaths.length > 0) {
          const isItemOpen = isOpen(item.path);

          return (
            <Fragment key={key}>
              <ListItem
                selected={isActivePath(pathTo)}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleOpen(item.path)}
                  >
                    {isItemOpen ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                }
              >
                <ListItemText>{item.label}</ListItemText>
              </ListItem>
              <Collapse in={isItemOpen}>
                <SidebarMenuLevel
                  navigationRootPath={navigationRootPath}
                  parentLevels={[...parentLevels, item.path]}
                  levelPaths={item.subPaths}
                />
              </Collapse>
            </Fragment>
          );
        }

        return (
          <ListItemButton component={CustomNavLink} to={pathTo} key={key}>
            <ListItemText>{item.label}</ListItemText>
          </ListItemButton>
        );
      })}
    </List>
  );
};
